/**
 * src/components/mfa/AdminSecurityPanel.tsx
 * 管理者向け「認証管理」: 在籍中アシスタントの認証状態の確認・リセット / 必須化スイッチ / 認証操作の監査ログ。
 * 認証コード・トークン・公開鍵などの秘密情報は API が返さないため、ここにも表示されない。
 * 対象は在籍中のアシスタントだけ（退職済みはサーバーが返さない）。表示するだけで、勤怠・給与などの過去データには触れない。
 */

import { useCallback, useEffect, useState } from 'react'
import { MfaApiError, METHOD_LABEL, mfaFetch, type MfaMethod } from '../../api/mfa'
import { useReauth } from './useReauth'

type AssistantRow = {
  user_id: string
  assistant_id: string | null
  name: string | null
  is_active: boolean
  employment_status: 'active' | 'leaving'
  login_enabled: boolean
  method: MfaMethod
  email_masked: string
  email_placeholder: boolean
  email_verified: boolean
  passkey_count: number
  needs_reset: boolean
  last_verified_at: string | null
  method_changed_at: string | null
}

type Policy = {
  admin_passkey_required: boolean
  assistant_mfa_required: boolean
  control_allowed: boolean
  environment: string
  readiness: {
    passkey_configured: boolean
    email_mfa_configured: boolean
    admins_total: number
    admins_with_passkey: number
  }
}

type EventRow = {
  id: string; event: string; success: number; method: string | null
  ip_address: string | null; detail: string | null; created_at: string; who: string | null
}

const EVENT_LABEL: Record<string, string> = {
  login_password_ok: 'パスワード認証OK',
  mfa_login_ok: '追加認証でログイン',
  mfa_stepup_ok: '再認証OK',
  mfa_passkey_failed: 'パスキー認証失敗',
  mfa_email_failed: 'メールコード不一致',
  mfa_email_sent: '認証コード送信',
  mfa_email_send_failed: '認証コード送信失敗',
  mfa_email_send_limited: '認証コード送信を制限',
  passkey_registered: 'パスキー登録',
  passkey_register_failed: 'パスキー登録失敗',
  passkey_deleted: 'パスキー削除',
  method_changed: '認証方式を変更',
  email_verified: 'メール確認',
  email_verify_failed: 'メール確認失敗',
  email_changed: 'メール変更',
  mfa_reset_by_admin: '管理者が認証をリセット',
  admin_recovery_used: '管理者復旧コード使用',
  admin_recovery_failed: '復旧コード失敗',
  password_changed: 'パスワード変更',
  password_reset_by_admin: '管理者がPW再設定',
  policy_changed: '必須化設定を変更',
}

function fmt(v: string | null): string {
  if (!v) return '—'
  const iso = v.includes('T') ? v : v.replace(' ', 'T') + 'Z'
  const d = new Date(iso)
  return isNaN(d.getTime()) ? v : d.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export default function AdminSecurityPanel({ isMobile }: { isMobile: boolean }) {
  const { withReauth, dialog } = useReauth()
  const [rows, setRows] = useState<AssistantRow[] | null>(null)
  const [policy, setPolicy] = useState<Policy | null>(null)
  const [events, setEvents] = useState<EventRow[] | null>(null)
  const [showEvents, setShowEvents] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [a, p] = await Promise.all([
        mfaFetch<{ assistants: AssistantRow[] }>('/auth/mfa/admin/assistants'),
        mfaFetch<Policy>('/auth/mfa/policy'),
      ])
      setRows(a.assistants); setPolicy(p)
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message })
    }
  }, [])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    if (!showEvents) return
    mfaFetch<{ events: EventRow[] }>('/auth/mfa/admin/events?limit=100').then((r) => setEvents(r.events)).catch(() => setEvents([]))
  }, [showEvents])

  const resetUser = async (r: AssistantRow) => {
    if (!window.confirm(`${r.name ?? 'このアシスタント'} の追加認証（パスキー・メール確認）をリセットします。\nログイン中の端末はログアウトされ、次回ログイン時に再設定が必要になります。よろしいですか？`)) return
    setMsg(null); setBusy(`reset:${r.user_id}`)
    try {
      await withReauth((extra) => mfaFetch(`/auth/mfa/admin/assistants/${encodeURIComponent(r.user_id)}/reset`, 'POST', extra))
      setMsg({ ok: true, text: `${r.name ?? 'アシスタント'} の追加認証をリセットしました。` })
      await load()
    } catch (e) {
      if (!(e instanceof MfaApiError && e.code === 'CANCELLED')) setMsg({ ok: false, text: (e as Error).message })
    } finally { setBusy(null) }
  }

  const setPolicyFlag = async (key: 'admin_passkey_required' | 'assistant_mfa_required', value: boolean) => {
    const label = key === 'admin_passkey_required' ? '管理者の追加認証必須化' : 'アシスタントの追加認証必須化'
    const warn = value
      ? `${label}を ON にします。\n・対象ユーザーの現在のログイン（あなたの端末以外）はすべてログアウトされます。\n・未設定の対象ユーザーは、設定が完了するまで他の機能を使えなくなります。\nよろしいですか？`
      : `${label}を OFF にします。よろしいですか？`
    if (!window.confirm(warn)) return
    setMsg(null); setBusy(`policy:${key}`)
    try {
      await withReauth((extra) => mfaFetch('/auth/mfa/policy', 'PUT', { [key]: value, ...extra }))
      setMsg({ ok: true, text: `${label}を${value ? 'ON' : 'OFF'}にしました。` })
      await load()
    } catch (e) {
      if (!(e instanceof MfaApiError && e.code === 'CANCELLED')) setMsg({ ok: false, text: (e as Error).message })
    } finally { setBusy(null) }
  }

  const pad = isMobile ? '16px 16px' : '20px 24px'
  const toggle = (key: 'admin_passkey_required' | 'assistant_mfa_required', title: string, desc: string) => {
    if (!policy) return null
    const on = policy[key]
    return (
      <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', padding: '10px 0', borderBottom: '1px solid var(--paper-3)' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{title} <span className={`badge ${on ? 'badge-green' : 'badge-gray'}`}>{on ? 'ON' : 'OFF'}</span></div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.7, marginTop: 4 }}>{desc}</div>
        </div>
        <button className="btn btn-outline btn-sm" style={{ minHeight: 34 }}
          disabled={busy !== null || (!on && !policy.control_allowed)}
          onClick={() => void setPolicyFlag(key, !on)}>
          {busy === `policy:${key}` ? '変更中...' : on ? 'OFFにする' : 'ONにする'}
        </button>
      </div>
    )
  }

  return (
    <>
      {dialog}

      {/* アシスタントの認証状況 */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header"><span className="card-title">在籍中アシスタントの認証状態（確認・リセット）</span></div>
        <div style={{ padding: pad }}>
          {rows === null ? <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>読み込み中...</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {rows.length === 0 && <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>在籍中のアシスタントはいません。</div>}
              {rows.map((r) => (
                <div key={r.user_id} style={{
                  border: '1px solid var(--paper-3)', borderRadius: 10, padding: '12px 14px', background: 'var(--paper)',
                  display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10,
                  justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', opacity: r.login_enabled ? 1 : 0.6,
                }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>
                      {r.name ?? '(名称なし)'}{' '}
                      {r.employment_status === 'leaving' && <><span className="badge badge-amber">退職予定</span>{' '}</>}
                      {!r.login_enabled && <><span className="badge badge-gray">ログイン停止中</span>{' '}</>}
                      {r.needs_reset
                        ? <span className="badge badge-red">要リセット（ログイン停止中）</span>
                        : r.method === 'none' ? <span className="badge badge-amber">未設定</span> : <span className="badge badge-green">{METHOD_LABEL[r.method]}</span>}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.8, marginTop: 4 }}>
                      メール: {r.email_masked}{' '}
                      {r.email_placeholder ? '（仮）' : r.email_verified ? '（確認済み）' : '（未確認）'}
                      ／ パスキー: {r.passkey_count}件 ／ 最終認証: {fmt(r.last_verified_at)}
                    </div>
                  </div>
                  <button className="btn btn-outline btn-sm" style={{ minHeight: 34, color: 'var(--red)', flexShrink: 0 }}
                    disabled={busy !== null || (r.method === 'none' && r.passkey_count === 0 && !r.needs_reset)}
                    onClick={() => void resetUser(r)}>
                    {busy === `reset:${r.user_id}` ? 'リセット中...' : '認証をリセット'}
                  </button>
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.7, marginTop: 12 }}>
            認証コードやパスキーの中身は表示されません。メールが届かない・端末を替えたなどでログインできないアシスタントは、
            ここでリセットすると、次回ログイン時に認証方式を設定し直せます（実行時に本人確認が必要です）。
            退職済みのアシスタントはこの一覧に表示されません。給与・勤怠などの過去のデータは、この画面の操作では変更されません。
          </div>
          {msg && (
            <div role={msg.ok ? 'status' : 'alert'} style={{
              background: msg.ok ? 'var(--green-light)' : 'var(--red-light)', color: msg.ok ? 'var(--green)' : 'var(--red)',
              borderRadius: 8, padding: '10px 14px', fontSize: 13, marginTop: 12,
            }}>{msg.ok ? '✓ ' : '⚠ '}{msg.text}</div>
          )}
        </div>
      </div>

      {/* 必須化スイッチ */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header"><span className="card-title">追加認証の必須化（段階導入）</span></div>
        <div style={{ padding: pad }}>
          {!policy ? <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>読み込み中...</div> : (
            <>
              {!policy.control_allowed && (
                <div style={{ fontSize: 12.5, color: 'var(--amber)', background: 'var(--amber-light)', borderRadius: 8, padding: '10px 12px', marginBottom: 12, lineHeight: 1.7 }}>
                  🔒 追加認証の必須化は、現在は利用できません。パスキーの設定と復旧手順の確認が終わったあと、システム保守担当者が有効にします。
                  それまでは、各自が追加認証を「任意で」設定できます。
                </div>
              )}
              {toggle('admin_passkey_required', '管理者の追加認証必須化',
                'ON にすると、パスキー未登録の管理者は登録が完了するまで管理機能を使えません（最初の登録には、システム保守担当者が発行する登録コードが必要）。')}
              {toggle('assistant_mfa_required', 'アシスタントの追加認証必須化',
                'ON にすると、追加認証が未設定のアシスタントは、設定が完了するまで日報・勤怠などを使えません。')}
              <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.9, marginTop: 12 }}>
                <div>準備状況（ONにする前の確認）:</div>
                <div>{policy.readiness.passkey_configured ? '✓ パスキー: 利用できます' : '✗ パスキー: 準備中です'}</div>
                <div>{policy.readiness.email_mfa_configured ? '✓ メール認証: 利用できます' : '✗ メール認証: 準備が完了していません'}</div>
                <div>{policy.readiness.admins_with_passkey >= policy.readiness.admins_total && policy.readiness.admins_total > 0 ? '✓' : '△'} 管理者のパスキー登録: {policy.readiness.admins_with_passkey} / {policy.readiness.admins_total} 人</div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* 監査ログ */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <span className="card-title">認証操作のログ</span>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowEvents((v) => !v)}>{showEvents ? '閉じる' : '表示する'}</button>
        </div>
        {showEvents && (
          <div style={{ padding: pad, overflowX: 'auto' }}>
            {events === null ? <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>読み込み中...</div> : (
              <table style={{ width: '100%', fontSize: 12.5 }}>
                <thead><tr><th>日時</th><th>対象</th><th>操作</th><th>結果</th></tr></thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmt(e.created_at)}</td>
                      <td>{e.who ?? '—'}</td>
                      <td>{EVENT_LABEL[e.event] ?? e.event}</td>
                      <td>{e.success ? <span className="badge badge-green">OK</span> : <span className="badge badge-red">NG</span>}</td>
                    </tr>
                  ))}
                  {events.length === 0 && <tr><td colSpan={4} style={{ color: 'var(--ink-3)' }}>ログはまだありません。</td></tr>}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </>
  )
}
